import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import { useSalesScope } from './salesTargetAccess';
import OrganizationTree from './OrganizationTree';
import SalesReportsTab from './SalesReportsTab';
import SalesmenTab from './SalesmenTab';
import SalesAnalysisTab from './SalesAnalysisTab';
import MtdDistributionTab from './MtdDistributionTab';
import YoyComparisonTab from './YoyComparisonTab';
import SalesAutomationLogsTab from './SalesAutomationLogsTab';
import BranchMappingTab from './BranchMappingTab';
import './SalesTargetManagement.css';

const ALL_TABS = [
    {
        id: 'tree',
        label: 'Organization Tree',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3h6v4H3zM15 3h6v4h-6zM9 17h6v4H9z"/>
                <line x1="6" y1="7" x2="6" y2="14"/>
                <line x1="18" y1="7" x2="18" y2="12"/>
                <line x1="12" y1="21" x2="12" y2="12"/>
                <line x1="6" y1="14" x2="18" y2="14"/>
                <line x1="12" y1="14" x2="12" y2="17"/>
            </svg>
        ),
        requiresPermission: false
    },
    {
        id: 'analysis',
        label: 'Sales Analysis',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="20" x2="12" y2="10"/>
                <line x1="18" y1="20" x2="18" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="16"/>
            </svg>
        ),
        requiresPermission: false
    },
    {
        id: 'mtd',
        label: 'MTD Distribution',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="9" y1="14" x2="9" y2="18"/>
                <line x1="15" y1="14" x2="15" y2="18"/>
            </svg>
        ),
        requiresPermission: false
    },
    {
        id: 'yoy',
        label: 'YoY Comparison',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                <polyline points="16 7 22 7 22 13"/>
            </svg>
        ),
        requiresPermission: false
    },
    {
        id: 'reports',
        label: 'Sales Reports',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M8 17V13M12 17V9M16 17V12"/>
            </svg>
        ),
        requiresPermission: true,
        requiredActions: ['create', 'edit']
    },
    {
        id: 'salesmen',
        label: 'Salesmen',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
        ),
        requiresPermission: true,
        requiredActions: ['create', 'edit']
    },
    {
        id: 'automation-logs',
        label: 'Automation Logs',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
            </svg>
        ),
        requiresPermission: false
    },
    {
        id: 'branch-mapping',
        label: 'Branch Mapping',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
        ),
        // Editing a link changes who can see which salesmen, so it sits behind
        // the same actions as creating/editing an organization — and, since it
        // lists the whole group and the server refuses a link change from anyone
        // limited to some branches, only for users who see every branch.
        requiresPermission: true,
        requiredActions: ['create', 'edit'],
        requiresUnrestricted: true
    }
];

const SalesTargetManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('tree');
    const scope = useSalesScope(user);
    const organizationTreeRef = useRef();

    // Filter tabs based on user permissions
    const TABS = useMemo(() => {
        if (!user) return ALL_TABS.filter(tab => !tab.requiresPermission);
        
        return ALL_TABS.filter(tab => {
            // Always show tabs that don't require permission
            if (!tab.requiresPermission) return true;
            
            // Check if user has any of the required actions
            const hasRequiredPermission = user.role === 'system_admin' || tab.requiredActions.some(action =>
                hasCardAction(user.permissions, 'sales-target-management', action)
            );

            // Hidden until the scope has loaded, rather than flashing in and out.
            if (tab.requiresUnrestricted && !scope?.unrestricted) return false;

            return hasRequiredPermission;
        });
    }, [user, scope]);

    // Function to refresh organization tree from other tabs
    const refreshOrganizationTree = () => {
        if (organizationTreeRef.current && organizationTreeRef.current.refreshTree) {
            organizationTreeRef.current.refreshTree();
        }
    };

    return (
        <div className="sales-target-container">
            {/* ── Header ── */}
            <div className="st-header">
                <div className="st-header-left">
                    <button
                        className="st-back-btn"
                        onClick={() => navigate('/purpose-selection')}
                        title="Back to menu"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        Back
                    </button>
                    <div className="st-title-block">
                        <h1>Sales Target Management</h1>
                        <p>Track performance, manage organizations & salesmen</p>
                    </div>
                </div>
                <div className="st-user-info">
                    <span className="user-label">Logged in as</span>
                    <span className="user-name">{user?.fullName || user?.username}</span>
                </div>
            </div>

            {/* ── Tab Navigation ── */}
            <div className="st-tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`st-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Tab Content ── */}
            <div className="st-content">
                {activeTab === 'tree'             && <OrganizationTree ref={organizationTreeRef} />}
                {activeTab === 'analysis'        && <SalesAnalysisTab />}
                {activeTab === 'mtd'             && <MtdDistributionTab />}
                {activeTab === 'yoy'             && <YoyComparisonTab />}
                {activeTab === 'reports'         && <SalesReportsTab onDataChange={refreshOrganizationTree} />}
                {activeTab === 'salesmen'        && <SalesmenTab />}
                {activeTab === 'automation-logs' && <SalesAutomationLogsTab />}
                {activeTab === 'branch-mapping'  && <BranchMappingTab />}
            </div>
        </div>
    );
};

export default SalesTargetManagement;
